// ** React Imports
import { useEffect, useMemo, useState } from "react";

// ** MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Backdrop,
  AvatarGroup,
  Grid,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Custom Component Imports
import CustomTextField from "../../../components/mui/text-field";
import CustomAutocomplete from "../../../components/mui/autocomplete";
import CustomChip from "../../../components/mui/chip";
import { stateNames } from "../../../components/US_StatesNames";
import { GetEntityFilter, GetfilteredRequests, SaveLinkRecord } from "../logic/LinkRecordLogic";

// ** Icon Imports
import Icon from "../../../components/icon";
import { ToastContainer, toast } from 'react-toastify';

// const getUniqueValues = (data, key) => {
//   return data && data.length > 0 ? [...new Set(data.map((item) => item[key]))] : [];
// };

// const getUniqueValues = (data, key) => {
//   return data && data.length > 0
//     ? [...new Set(data.map((item) => item[key] || ''))]
//     : [];
// };

const getUniqueValues = (data, key) => {
  return data && data.length > 0
    ? [...new Set(data.map((item) => item[key]).filter(Boolean))]
    : [];
};

const SearchRecords = ({ open, handleClose, requestID, customerID, fetchLinks, linkedRequests }) => {
  // Hooks
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // States
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [records, setRecords] = useState([]);  
  const [filterOptions, setFilterOptions] = useState([]);  
  const [loading, setLoading] = useState(false);
  const [allRequests, setAllRequests] = useState([]);
  const [totalRecords, setTotalRecords] = useState(null);   

  const [paginationModel, setPaginationModel] = useState({
    pageNo: 1, // Default to page 1
    recordsPerPage: 10000, // Fetch all records at once
  });

  const getInitialFilters = () => ({
    entityType: null,
    serviceName: null,
    jobSiteName: null,
    jobSiteType: null,
    propertyAreaName: null,
    propertyAreaType: null,
    status: null,
    tagName: null,
    
  });

  const [selectedFilters, setSelectedFilters] = useState(getInitialFilters);
  
  useEffect(() => {
    if (customerID && requestID && linkedRequests) {
      fetchFilterOptions(customerID);
      fetchFilterData(customerID, paginationModel.pageNo, paginationModel.recordsPerPage, requestID, selectedFilters.entityType || '', selectedFilters.status || '', selectedFilters.jobSiteName || '', selectedFilters.jobSiteType || '', selectedFilters.serviceName || '', selectedFilters.propertyAreaName || '', selectedFilters.propertyAreaType || '', '', selectedFilters.tagName || '');
    }
  }, [customerID, requestID, linkedRequests]);

  useEffect(() => {
    // When records change, ensure DataGrid updates
  }, [records]);
  
  
  const fetchFilterOptions = async (customerID) => {
    
    try {
      setLoading(true);
      let res = await GetEntityFilter(customerID);
      
      // console.log(res)
      // const filterOptions = res[0];      

      setFilterOptions(res);
      
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      setLoading(false);
    }
};

// useEffect(() => {  
//   if(customerID){
//     // console.log(request?.customerID)
//     fetchFilterData(customerID, paginationModel.pageNo, paginationModel.recordsPerPage, 0, selectedFilters.entityType || '', selectedFilters.status || '', selectedFilters.jobSiteName || '', selectedFilters.jobSiteType || '', selectedFilters.serviceName || '', selectedFilters.propertyAreaName || '', selectedFilters.propertyAreaType || '', '', selectedFilters.tagName || '');  
//   }  
// }, [customerID, selectedFilters]);

const fetchFilterData = async (customerID, pageNo, recordsPerPage, requestID, entityType, status, jobSiteName, jobSiteType, serviceName, propertyAreaName, propertyAreaType, userName, tagName) => {
    
  try {
    setLoading(true);
    let res = await GetfilteredRequests(customerID, pageNo, recordsPerPage, requestID, entityType, status, jobSiteName, jobSiteType, serviceName, propertyAreaName, propertyAreaType, userName, tagName);
    
    setTotalRecords(res?.totalRecords)
    setAllRequests(res?.result);
    // console.log(res?.result)

    const rows = res?.result
    .filter(request =>
      Number(request.requestID) !== Number(requestID) &&
      !linkedRequests.some(linked => Number(linked.requestID) === Number(request.requestID))
    )
    .map(request => ({
      id: request.requestID, // Using requestID as id
      recordType: request.entityType === "WorkOrder" ? "Work Order" : request.entityType,
      recordID: request.requestID,
      jobsiteName: request.jobSiteName,
      createDate: new Date(request?.createDate).toLocaleDateString(),
      status: request.status,
      assignedTo: request.userName,
      tags: request.tagName
    }));


    //  console.log(res?.result)
    setSelectedRows([])
    setRecords(rows);
    // console.log(rows)
    
  } catch (error) {
    console.error("Error fetching filter data:", error);
  } finally {
    setLoading(false);
  }
};

  const handleFilterChange = (key, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = async () => {
    await fetchFilterData(customerID, paginationModel.pageNo, paginationModel.recordsPerPage, requestID, selectedFilters.entityType || '', selectedFilters.status || '', selectedFilters.jobSiteName || '', selectedFilters.jobSiteType || '', selectedFilters.serviceName || '', selectedFilters.propertyAreaName || '', selectedFilters.propertyAreaType || '', '', selectedFilters.tagName || '');     
  }
  const applyLinkRecords = async () => {
    // Disable individual toasts by passing false
    const results = await Promise.all(
      selectedRows.map(record => {
        const payload = {
          id: 0,
          fromEntityId: requestID,
          toEntityId: record.id,
        };
        return SaveLinkRecord(payload, false);
      })
    );
  
    // Determine if there was any error
    const failedCount = results.filter(result => !result.success).length;
  
    if (failedCount > 0) {
      toast.error(`${failedCount} record(s) failed to link.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    } else {
      toast.success("All records linked successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    }
  
    setSelectedRows([]);
    closePopup();
    fetchLinks();
  };
  


  const resetfilter = async () => {
    setSelectedFilters(getInitialFilters());

    setPaginationModel({
      pageNo: 1, // Default to page 1
      recordsPerPage: 10000, // Fetch all records at once
    });
    await fetchFilterData(customerID, 1, 10000, requestID, '', '',  '', '',  '',  '',  '', '', '');  

  }

  const filteredOptions = filterOptions.filter((item) =>
    Object.keys(selectedFilters).every(
      (key) =>
        !selectedFilters[key] || // If no filter is selected for the key, include all options
        item[key] === selectedFilters[key] // Include options that match the selected filter
    )
  );

  const entityTypes = getUniqueValues(filteredOptions, 'entityType');
  const services = getUniqueValues(filteredOptions, 'serviceName');
  const jobSiteNames = getUniqueValues(filteredOptions, 'jobSiteName');
  const jobSiteTypes = getUniqueValues(filteredOptions, 'jobSiteType');
  const propertyAreaNames = getUniqueValues(filteredOptions, 'propertyAreaName');
  const propertyAreaTypes = getUniqueValues(filteredOptions, 'propertyAreaType');
  const statuses = getUniqueValues(filteredOptions, 'status');
  const tags = getUniqueValues(filteredOptions, 'tagName');

  function getInitials(name) {
    if (!name) return "";
  
    return name
      .split(" ")
      .map(word => word[0]?.toUpperCase())
      .join("");
  }

  // Pagination handler removed - now fetching all records at once
  // const handlePaginationModelChange = (model) => {
  //   const newPageNo = model.page + 1; // Convert zero-based index to one-based
  //   const newRecordsPerPage = model.pageSize;
  
  //   setPaginationModel({
  //     pageNo: newPageNo,
  //     recordsPerPage: newRecordsPerPage,
  //   });

  //   setSelectedRows([])
  //   fetchFilterData(customerID, newPageNo, newRecordsPerPage, requestID, selectedFilters.entityType || '', selectedFilters.status || '', selectedFilters.jobSiteName || '', selectedFilters.jobSiteType || '', selectedFilters.serviceName || '', selectedFilters.propertyAreaName || '', selectedFilters.propertyAreaType || '', '', selectedFilters.tagName || '');  
  // };

  const closePopup = () => {

    setSelectedFilters(getInitialFilters());

    setPaginationModel({
      pageNo: 1, // Default to page 1
      recordsPerPage: 10000, // Fetch all records at once
    });
    resetfilter();
    setSelectedRows([]);
    handleClose();
  }

  const columns = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "recordID",
      headerName: "Record ID",
      renderCell: (params) => {
        const { row } = params;
        const prefixMap = {
          Request: "REQ",
          Assessment: "AST",
          Estimate: "EST",
          ["Work Order"]: "WO",
          Invoice: "INV",
          Customer: "CUS",
        };

        const prefix = prefixMap[row.recordType] || "";
        const displayId = prefix
          ? `${prefix}-${row?.recordID?.toString().padStart(3, "0")}`
          : row.recordID

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {displayId}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 160,
      field: "recordType",
      headerName: "Record Type",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {row.recordType}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 170,
      field: "jobsiteName",
      headerName: "Jobsite Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {row.jobsiteName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      type: "date",
      minWidth: 160,
      headerName: "Created Date",
      field: "createdDate",
      // valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {params.row.createDate}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      minWidth: 110,
      field: "status",
      headerName: "Status",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {/* <CustomChip
                rounded
                label={params.row.status}
                size="small"
                color={params.row.status === "Active" ? 'success' : params.row.status === "Inactive" ? 'secondary' : null }
                skin="light"
                sx={{ lineHeight: 1 }}
            /> */}
            {params.row.status}
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "assignedTo",
      minWidth: 130,
      headerName: "Assigned To",
      renderCell: (params) => {
        const { row } = params;
        const initials = getInitials(row.assignedTo)
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <AvatarGroup 
              className='pull-up' 
              max={4} 
              sx={{
                "& .MuiAvatar-root": { width: 30, height: 30 },
                "& .MuiAvatarGroup-avatar": { width: 30, height: 30 },
              }}
            >
              <Tooltip title={row.assignedTo}>
                <Avatar alt='Olivia Sparks' sx={{ fontSize: '13px' }}>{initials}</Avatar>
              </Tooltip>
            </AvatarGroup>
          </Box>
        )
      },
    },
    {
      flex: 0.1,
      field: "tags",
      minWidth: 250,
      headerName: "Tags",
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "nowrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            {row.tags && row.tags.length > 0 ? (
            row.tags.split(',').map((tag, index) => (
              <CustomChip
                key={index}
                rounded
                label={tag.trim()} // Trim to remove any extra spaces
                size="small"
                color="info"
                skin="light"
                sx={{ lineHeight: 1 }}
              />
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No tags
            </Typography>
          )}
            {/* {console.log(row.tags)} */}
          </Box>
        );
      },
    },
  ];

  // const handleGridSelection = (selectedRows) => {
  //   setSelectedRows(selectedRows);
  // };
  const handleRowSelectionChange = (newSelectionModel) => {
    setRowSelectionModel(newSelectionModel);

    // Extract the selected rows' data
    const selectedRowData = records.filter((record, index) => {
      const uniqueId = `${record.recordID}-${index}`;
      return newSelectionModel.includes(uniqueId);
    });
    setSelectedRows(selectedRowData);
  };


  return (
  <Dialog
  fullWidth={true}
  maxWidth="xl"
  open={open}
  onClose={closePopup}
  aria-labelledby="form-dialog-title"
  PaperProps={{
    sx: { maxHeight: "90vh" },
  }}
>
  <DialogTitle
    sx={{
      borderBottom: "1px solid #D9D9D9",
      paddingBottom: "20px !important",
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
        Search Records to Link
      </Typography>
      <Tooltip title='Close'>
        <IconButton onClick={closePopup}>
          <Icon icon='tabler:x' />
        </IconButton>
      </Tooltip>
    </Box>
  </DialogTitle>
  <DialogContent
    sx={{
      height: '100%',
      p: { xs: 2, md: 3 } // Adjust padding for different screens
    }}
  >
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={2}> 
        <Card
          sx={{
            display: "flex",
            flexDirection: { xs: 'column', md: 'column' }, 
            gap: 3,
            maxHeight: "calc(90vh - 200px)",
            p: { xs: 2, md: 3 },
            width: '100%',
            overflowY: 'auto'
          }}
        >
          <Typography variant='h5' sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Filters:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              overflow: "auto",
              width: '100%',
            }}
          >
            <CustomAutocomplete
              id="recordtype-name-autocomplete"
              options={entityTypes.filter((item) => item)}
              getOptionLabel={(option) => {
                const label = typeof option === "string" ? option : option?.label;
                return label === "WorkOrder" ? "Work Order" : label || "";
              }}
              value={selectedFilters.entityType}
              onChange={(event, newValue) => handleFilterChange('entityType', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Record Type" {...params} />
              )}
              renderOption={(props, option) => {
                const label = typeof option === "string" ? option : option?.label;
                return (
                  <li {...props}>
                    {label === "WorkOrder" ? "Work Order" : label || ""}
                  </li>
                );
              }}
            />
            <CustomAutocomplete
              id="services-name-autocomplete"
              options={services.filter((item) => item)}
              value={selectedFilters.serviceName}
              onChange={(event, newValue) => handleFilterChange('serviceName', newValue)}
              getOptionLabel={(option) => option?.label || option || ''}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Service" {...params} />
              )}
            />
            <CustomAutocomplete
              id="jobsite-name-autocomplete"              
              options={jobSiteNames.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}
              value={selectedFilters.jobSiteName}
              onChange={(event, newValue) => handleFilterChange('jobSiteName', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Jobsite Name" {...params} />
              )}
            />
    
            <CustomAutocomplete
              id="jobSiteType-name-autocomplete"              
              options={jobSiteTypes.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}
              value={selectedFilters.jobSiteType}
              onChange={(event, newValue) => handleFilterChange('jobSiteType', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Jobsite Type" {...params} />
              )}
            />
            <CustomAutocomplete
              id="property-area-autocomplete"              
              options={propertyAreaNames.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}              
              value={selectedFilters.propertyAreaName}
              onChange={(event, newValue) => handleFilterChange('propertyAreaName', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Property Area" {...params} />
              )}
            />
          
            <CustomAutocomplete
              id="propertyAreaType-name-autocomplete"
              options={propertyAreaTypes.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}                            
              value={selectedFilters.propertyAreaType}
              onChange={(event, newValue) => handleFilterChange('propertyAreaType', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Property Area Type" {...params} />
              )}
            />
         
            <CustomAutocomplete
              id="status-name-autocomplete"
              options={statuses.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}              
              value={selectedFilters.status}
              onChange={(event, newValue) => handleFilterChange('status', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Status" {...params} />
              )}
            />

            <CustomAutocomplete
              id="tag-name-autocomplete"
              options={tags.filter((item) => item)}
              getOptionLabel={(option) => option?.label || option || ''}
              value={selectedFilters.tagName}
              onChange={(event, newValue) => handleFilterChange('tagName', newValue)}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <CustomTextField fullWidth placeholder="Tags" {...params} />
              )}
            />
          </Box>
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: 'column', md: 'column' }, 
            gap: 2,
            width: '100%',
            mt: 2
          }}>
            <Button variant='contained' color='main' onClick={applyFilter} sx={{ flex: 1 }}>Apply Filter</Button>
            <Button variant='outlined' color={theme.palette.mode === 'dark' ? "secondary" : "primary"} onClick={resetfilter} sx={{ flex: 1 }}>Reset</Button>
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} lg={10}> 
        <Box>
          <Card>
            <CardHeader 
              title={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    alignItems: { xs: 'flex-start', md: 'center' }
                  }}
                >
                  {/* <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    Displaying Records for John Doe
                  </Typography> */}
                </Box>
              }
            />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "calc(90vh - 290px)",
              }}
            >
              <Box 
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "hidden",
                    overflowX: "auto",
                  }}
                >
                  
                  <DataGridPro
                    rows={records.map((record, index) => ({
                      ...record,
                      id: `${record.recordID}-${index}`,
                    }))}
                    columns={columns}
                    checkboxSelection
                    rowHeight={62}
                    onSelectionModelChange={handleRowSelectionChange}
                    sx={{
                      "& .MuiDataGrid-columnHeaderTitle": {
                        textTransform: "none",
                      },
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Grid>
    </Grid>
  
 

           
        {/* </Box> */}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="contained" color="info" onClick={closePopup}>
            Cancel
          </Button>
        </Box>
        <Box>
          <Button disabled={selectedRows.length === 0} variant="contained" color="main" onClick={applyLinkRecords}>
            Link { selectedRows.length === 1 ? `${selectedRows.length} Record` : selectedRows.length > 1 ? `${selectedRows.length} Records` : null }
          </Button>
        </Box>
      </DialogActions>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default SearchRecords;
